/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Math Term Matching Game Page
 *
 * Interactive game where students match math vocabulary terms with their
 * definitions. Tracks time and attempts, supports standard (6 pairs) and
 * hard (12 pairs) modes.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Timer, RotateCcw, Trophy, Zap, ArrowLeft } from 'lucide-react'
import MatchingCard from '../components/game/MatchingCard'
import vocabService from '../services/vocabService'
import LoadingSpinner from '../components/LoadingSpinner'
import { createLogger } from '../utils/logger'

const logger = createLogger('MathMatchingGame')

// Shuffle an array using Fisher-Yates
function shuffle(array) {
  const arr = [...array]
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

// Format seconds into m:ss
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const MathMatchingGame = () => {
  const navigate = useNavigate()

  // Game state: idle, playing, completed
  const [gameState, setGameState] = useState('idle')
  const [cards, setCards] = useState([])
  const [selectedCard, setSelectedCard] = useState(null)
  const [matchedPairs, setMatchedPairs] = useState(new Set())
  const [incorrectPair, setIncorrectPair] = useState(null)
  const [attempts, setAttempts] = useState(0)
  const [timer, setTimer] = useState(0)
  const [hardMode, setHardMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [totalPairs, setTotalPairs] = useState(0)
  const timerRef = useRef(null)

  // Start the timer
  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setTimer((prev) => prev + 1)
    }, 1000)
  }, [])

  // Stop the timer
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  // Clean up timer on unmount
  useEffect(() => {
    return () => stopTimer()
  }, [stopTimer])

  // Initialize a new game
  const startGame = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      stopTimer()
      setTimer(0)
      setAttempts(0)
      setMatchedPairs(new Set())
      setSelectedCard(null)
      setIncorrectPair(null)

      const pairCount = hardMode ? 12 : 6

      // Exclude recently used terms to avoid repetition across games
      const recentKey = 'matching_game_recent_ids'
      const recentIds = JSON.parse(sessionStorage.getItem(recentKey) || '[]')
      const result = await vocabService.getMathTerms(pairCount, 'math', recentIds)

      if (!result.success || !result.data || result.data.length < pairCount) {
        // Retry without exclusions
        const fallback = await vocabService.getMathTerms(pairCount, 'math')
        if (!fallback.success || !fallback.data || fallback.data.length < pairCount) {
          setError(`Not enough math terms available. Need ${pairCount} terms.`)
          return
        }
        result.data = fallback.data
      }

      const terms = result.data
      setTotalPairs(terms.length)

      // Track used term IDs (keep last 3 rounds worth)
      const newIds = terms.map((t) => t.id)
      const updatedRecent = [...recentIds, ...newIds].slice(-(pairCount * 3))
      sessionStorage.setItem(recentKey, JSON.stringify(updatedRecent))

      // Create term cards and definition cards
      const termCards = terms.map((t) => ({
        id: `term-${t.id}`,
        pairId: t.id,
        text: t.word,
        type: 'term'
      }))
      const defCards = terms.map((t) => ({
        id: `def-${t.id}`,
        pairId: t.id,
        text: t.definition,
        type: 'definition'
      }))

      setCards(shuffle([...termCards, ...defCards]))
      setGameState('playing')
      startTimer()
    } catch (err) {
      logger.error('Error starting game:', err)
      setError('Failed to load math terms. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [hardMode, startTimer, stopTimer])

  // Handle card click
  const handleCardClick = useCallback(
    (card) => {
      if (gameState !== 'playing') return
      if (matchedPairs.has(card.pairId)) return
      if (incorrectPair) return
      if (selectedCard?.id === card.id) {
        setSelectedCard(null)
        return
      }

      if (!selectedCard) {
        setSelectedCard(card)
        return
      }

      // Second card selected -- check for match
      setAttempts((prev) => prev + 1)

      // Must be different types (term + definition) and same pairId
      if (selectedCard.type !== card.type && selectedCard.pairId === card.pairId) {
        // Correct match
        const newMatched = new Set(matchedPairs)
        newMatched.add(card.pairId)
        setMatchedPairs(newMatched)
        setSelectedCard(null)

        // Check if game is complete
        if (newMatched.size === totalPairs) {
          stopTimer()
          setGameState('completed')
        }
      } else {
        // Incorrect match -- flash red briefly
        setIncorrectPair({ card1: selectedCard, card2: card })
        setTimeout(() => {
          setIncorrectPair(null)
          setSelectedCard(null)
        }, 300)
      }
    },
    [gameState, selectedCard, matchedPairs, incorrectPair, totalPairs, stopTimer]
  )

  // Determine card state for rendering
  const getCardState = (card) => {
    if (matchedPairs.has(card.pairId)) return 'correct'
    if (incorrectPair) {
      if (incorrectPair.card1.id === card.id || incorrectPair.card2.id === card.id) {
        return 'incorrect'
      }
    }
    if (selectedCard?.id === card.id) return 'selected'
    return 'default'
  }

  const accuracy = attempts > 0 ? Math.round((matchedPairs.size / attempts) * 100) : 0

  // Idle state -- show start screen
  if (gameState === 'idle' && !loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <button
          onClick={() => navigate('/games')}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Practice Games
        </button>

        <div className="card">
          <div className="card-body text-center space-y-6 py-10">
            <div className="inline-flex p-4 bg-primary-100 dark:bg-primary-900/30 rounded-2xl">
              <Zap className="w-12 h-12 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Math Term Matching Game
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Match math vocabulary terms with their definitions as fast as you can!
              </p>
            </div>

            {/* Hard Mode Toggle */}
            <div className="flex items-center justify-center gap-3">
              <label
                className="relative inline-flex items-center cursor-pointer"
                htmlFor="hard-mode-toggle"
              >
                <input
                  id="hard-mode-toggle"
                  type="checkbox"
                  checked={hardMode}
                  onChange={(e) => setHardMode(e.target.checked)}
                  className="sr-only peer"
                  data-testid="hard-mode-toggle"
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
                <span className="ms-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hard Mode ({hardMode ? '12' : '6'} pairs)
                </span>
              </label>
            </div>

            <button
              onClick={startGame}
              data-testid="start-game-btn"
              className="btn btn-primary px-8 py-3 text-lg"
            >
              Start Game
            </button>

            {error && (
              <p className="text-red-600 dark:text-red-400 text-sm" data-testid="game-error">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="game-loading">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Completed state
  if (gameState === 'completed') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="card">
          <div className="card-body text-center space-y-6 py-10">
            <div className="inline-flex p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-2xl">
              <Trophy className="w-12 h-12 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Game Complete!
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                You matched all {totalPairs} pairs{hardMode ? ' in Hard Mode' : ''}!
              </p>
            </div>

            {/* Results */}
            <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto" data-testid="game-results">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {formatTime(timer)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Time</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{attempts}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Attempts</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{accuracy}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Accuracy</p>
              </div>
            </div>

            <button
              onClick={() => {
                setGameState('idle')
                startGame()
              }}
              data-testid="play-again-btn"
              className="btn btn-primary px-8 py-3 text-lg flex items-center gap-2 mx-auto"
            >
              <RotateCcw className="w-5 h-5" />
              Play Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Playing state
  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Back link */}
      <button
        onClick={() => navigate('/games')}
        className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Practice Games
      </button>

      {/* Game Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Math Term Matching{hardMode ? ' (Hard)' : ''}
        </h1>
        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-1.5">
            <Timer className="w-4 h-4" />
            <span data-testid="game-timer">{formatTime(timer)}</span>
          </div>
          <span data-testid="game-attempts">
            {attempts} attempt{attempts !== 1 ? 's' : ''}
          </span>
          <span data-testid="game-matched">
            {matchedPairs.size}/{totalPairs} matched
          </span>
        </div>
      </div>

      {/* Card Grid */}
      <div
        className={`grid gap-3 ${
          hardMode
            ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
            : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
        }`}
        data-testid="card-grid"
      >
        {cards.map((card) => (
          <MatchingCard
            key={card.id}
            text={card.text}
            type={card.type}
            state={getCardState(card)}
            onClick={() => handleCardClick(card)}
            disabled={matchedPairs.has(card.pairId)}
          />
        ))}
      </div>
    </div>
  )
}

export default MathMatchingGame
